import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Button, InputNumber, Spin, Tooltip, Typography } from "antd";
import { BellOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNotifications } from "../../../contexts/NotificationContext";
import { useFlightModel } from "../../../contexts/FlightModelContext";
import { useAppMessage } from "../../../hooks/useAppMessage";
import { DefectCountThresholdAPI } from "../../../API/admin/DefectCountThresholdAPI";
import AdminHeaderTab from "../../../Components/headerTab/AdminHeaderTab";

const { Text, Title } = Typography;

const MIN_THRESHOLD = 100;
const MAX_THRESHOLD = 9999;

export default function DefectCountThreshold() {
  const { thresholdConfig, updateThresholdConfig } = useNotifications();
  const { selectedFlightModel, refreshFlightModels } = useFlightModel();
  const [messageApi, contextHolder] = useAppMessage();

  const [value, setValue] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchThreshold = useCallback(async () => {
    let flightModelToUse = selectedFlightModel;
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) return;

    setLoading(true);
    try {
      const res = await DefectCountThresholdAPI.getThreshold(flightModelToUse);

      if (res?.data && typeof res.data.threshold_pct !== "undefined") {
        const fetchedValue = Number(res.data.threshold_pct);
        setValue(fetchedValue);
        updateThresholdConfig({ threshold: fetchedValue }, { silent: true });
      } else {
        messageApi.error(res?.data?.message || "Failed to load defect count threshold.");
      }
    } catch (err) {
      messageApi.error("Internal Server Error");
    } finally {
      setLoading(false);
    }
  }, [selectedFlightModel, refreshFlightModels, updateThresholdConfig, messageApi]);

  useEffect(() => {
    fetchThreshold();
  }, [fetchThreshold]);

  function validate(val) {
    if (val === "" || val === null || val === undefined) {
      return "Defect count threshold is required.";
    }
    if (!Number.isInteger(val)) {
      return "Enter a whole number.";
    }
    if (val <= MIN_THRESHOLD) {
      return `Enter a value greater than ${MIN_THRESHOLD}.`;
    }
    if (val > MAX_THRESHOLD) {
      return `Enter a value not greater than ${MAX_THRESHOLD}.`;
    }
    return "";
  }

  function handleReset() {
    setValue(thresholdConfig.threshold);
    setError("");
  }

  async function handleSave() {
    const validationError = validate(value);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");

    let flightModelToUse = selectedFlightModel;
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageApi.error("No flight model selected.");
      return;
    }

    setSaving(true);
    try {
      const res = await DefectCountThresholdAPI.saveThreshold(flightModelToUse, {
        threshold_pct: value,
      });

      if (res?.data?.status === true) {
        updateThresholdConfig({ threshold: value }, { silent: true });
        messageApi.success(res.data.message || "Defect count threshold saved");
      } else if (Array.isArray(res?.data?.detail)) {
        // FastAPI validation error (e.g. HTTP 422): no "data" wrapper, just a detail list.
        messageApi.error(res.data.detail[0]?.msg || "Failed to save defect count threshold.");
      } else {
        messageApi.error(res?.data?.message || "Failed to save defect count threshold.");
      }
    } catch (err) {
      messageApi.error("Internal Server Error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container_div">
      <Helmet>
        <title>SpecOptimizer | Admin</title>
      </Helmet>

      <AdminHeaderTab currrentTab="Defect Count Threshold" />

      <Spin spinning={saving || loading}>
      <div className="admin_tab_content_wraper">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 24 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "#e8edf8",
              color: "#304cb2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            <BellOutlined />
          </div>
          <div>
            <Title level={4} style={{ margin: "0 0 4px", color: "#1e1b4b" }}>
              Defect Count Threshold
            </Title>
            <Text type="secondary" style={{ fontSize: 13, display: "block" }}>
              Alert admins when a spec's defect rate in a new upload exceeds this % of its rate
              at Optimization Completed time.
            </Text>
          </div>
        </div>

        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "22px 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: 190,
                flexShrink: 0,
              }}
            >
              <Text strong style={{ color: "#1e293b", marginRight: 8 }}>
                Defect Count Threshold
              </Text>
              <Tooltip title="Alert admins when a spec's defect rate in a new upload exceeds this % of its rate at Optimization Completed time.">
                <InfoCircleOutlined style={{ color: "#94a3b8", fontSize: 14 }} />
              </Tooltip>
            </div>
            <InputNumber
              value={value}
              status={error ? "error" : ""}
              controls={false}
              suffix="%"
              precision={0}
              onKeyDown={(e) => {
                if (
                  !/[\d]/.test(e.key) &&
                  !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              parser={(val) => (val || "").replace(/[^\d]/g, "")}
              onChange={(val) => {
                setValue(val);
                setError(validate(val));
              }}
              style={{ width: 200 }}
              size="large"
            />
          </div>
          {error && (
            <Text type="danger" italic style={{ display: "block", marginTop: 10, fontSize: 12 }}>
              {error}
            </Text>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
          <Button onClick={handleReset} disabled={saving}>
            Reset
          </Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save
          </Button>
        </div>
      </div>
      </Spin>

      {contextHolder}
    </div>
  );
}
